import type { QueryClient } from '@tanstack/react-query'

/**
 * Fired on `window` once the server has discarded the draft behind a recording
 * the clinician threw away (CLA-1875; `cancelRecording` in stores/recording).
 * The discard broadcasts no `note_changed`, so nothing else tells the screens
 * that showed the draft as a live recording.
 */
export const RECORDING_DRAFT_DISCARDED = 'claro:recording-draft-discarded'

export interface RecordingDraftDiscardedDetail {
  noteId: number
  noteUuid: string | null
}

/**
 * Fired on `window` once the server has taken a thrown-away recording off a
 * note that is KEPT (`abandon_capture`, CLA-1880): the note is no longer
 * `recording`. That broadcasts nothing either. Screens stay on the note.
 */
export const RECORDING_CAPTURE_ABANDONED = 'claro:recording-capture-abandoned'

export type RecordingCaptureAbandonedDetail = RecordingDraftDiscardedDetail

/**
 * Refresh what showed the recording: the notes list, the patient notes lists
 * and the note itself. Both lists leave discarded notes out, so after a
 * discard the refetch drops it; after an abandon it shows the kept note as it
 * is now. Installed once, on the app's query client (main.tsx). Returns the
 * unsubscribe.
 */
export function refreshOnRecordingDraftDiscarded(queryClient: QueryClient): () => void {
  const onChanged = (e: Event) => {
    const { noteId, noteUuid } = (e as CustomEvent<RecordingDraftDiscardedDetail>).detail
    queryClient.invalidateQueries({ queryKey: ['notes'] })
    queryClient.invalidateQueries({ queryKey: ['patient-notes'] })
    queryClient.invalidateQueries({ queryKey: ['note', noteId] })
    if (noteUuid) queryClient.invalidateQueries({ queryKey: ['note', noteUuid] })
  }
  window.addEventListener(RECORDING_DRAFT_DISCARDED, onChanged)
  window.addEventListener(RECORDING_CAPTURE_ABANDONED, onChanged)
  return () => {
    window.removeEventListener(RECORDING_DRAFT_DISCARDED, onChanged)
    window.removeEventListener(RECORDING_CAPTURE_ABANDONED, onChanged)
  }
}
